import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Send, Mail, Linkedin, Github, MapPin, Phone, ArrowUpRight, Download, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import BackToTopButton from '../components/BackToTopButton';


const CONTACT_DATA = {
  hero: {
    label: "contact.25",
    title: "GET IN",
    titleLine2: "TOUCH",
    description: "Have a project in mind? Or just a question? I'd love to help. Send a message and I'll get back to you as soon as possible.",
  },

  info: {
    email: "bekirhan-simsek@hotmail.com",
    phone: "+31 6 15 36 59 70",
    location: "Arendonk, Belgium",
  },

  socials: [
    { name: "LinkedIn", icon: Linkedin, url: "https://www.linkedin.com/in/bekirhan-simsek-5b675a358/" },
    { name: "GitHub", icon: Github, url: "https://github.com/BekirhanSimsek" },
    { name: "Email", icon: Mail, url: "mailto:bekirhan-simsek@hotmail.com" },
  ],

  faq: [
    {
      q: "What is your availability?",
      a: "I'm currently available for freelance projects and collaborations. Get in touch to discuss my current availability."
    },
    {
      q: "What kind of projects do you take on?",
      a: "I work on web applications, mobile apps, UI/UX design, and full-stack development projects. From small websites to complex applications."
    },
    {
      q: "How long does a typical project take?",
      a: "This depends on the scope. Small projects can take 2-4 weeks, while larger applications can take 2-3 months or more."
    },
    {
      q: "Do you work remote or on-site?",
      a: "I primarily work remotely, but I'm available for on-site work in the Benelux region when needed."
    },
  ],

  ticker: ["CONTACT", "COLLABORATE", "PROJECT", "IDEAS"],
};


// Progress Bar
function ProgressBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });

  return (
    <motion.div
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 origin-left z-[100]"
    />
  );
}

// FAQ Item Component
function FAQItem({ faq, index }) {
  const [isOpen, setIsOpen] = useState(index === 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-white/10 rounded-[24px] overflow-hidden hover:border-orange-500/50 transition-all group"
    >
      {/* Animated gradient border effect */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: 'linear-gradient(45deg, transparent, rgba(249, 115, 22, 0.1), transparent)',
          backgroundSize: '200% 200%',
        }}
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
        }}
        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
      />

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative w-full flex items-center justify-between p-6 lg:p-8 text-left group/button"
      >
        <div className="flex items-start gap-4 flex-1 pr-4">
          <motion.div
            animate={{ scale: isOpen ? 1.1 : 1 }}
            transition={{ duration: 0.3 }}
            className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-xl flex items-center justify-center"
          >
            <span className="text-orange-500 font-silkscreen text-sm">0{index + 1}</span>
          </motion.div>
          <span className="text-base lg:text-lg font-silkscreen text-white group-hover/button:text-orange-500 transition-colors">
            {faq.q}
          </span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 135 : 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center border border-orange-500/30 group-hover/button:border-orange-500 group-hover/button:bg-orange-500 transition-all"
        >
          <svg
            className="w-5 h-5 text-orange-500 group-hover/button:text-white transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M12 4v16m8-8H4"
            />
          </svg>
        </motion.div>
      </button>

      <motion.div
        initial={false}
        animate={{
          height: isOpen ? "auto" : 0,
          opacity: isOpen ? 1 : 0,
        }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
        className="overflow-hidden"
      >
        <div className="px-6 lg:px-8 pb-6 lg:pb-8">
          <motion.div
            initial={{ y: -10 }}
            animate={{ y: 0 }}
            className="pl-14 pt-2 border-t border-white/5"
          >
            <p className="text-white/70 leading-relaxed mt-4 text-sm lg:text-base">
              {faq.a}
            </p>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}



function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        message: formData.message,
        to_name: 'Bekirhan'
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      setLoading(false);
      setSubmitted(true);

      setTimeout(() => {
        setSubmitted(false);
        setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      }, 4000);
    } catch (error) {
      console.error('Failed to send email:', error);
      setLoading(false);
      alert('Failed to send message. Please try again or contact me directly at contact@bekirhan.be');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-[#000000] text-white min-h-screen"
    >
      {/* Progress Bar */}
      <ProgressBar />

      <section className="pt-24 sm:pt-32 pb-8 sm:pb-12 px-4 sm:px-6 lg:px-12 relative overflow-hidden rounded-b-[24px] sm:rounded-b-[40px]">
        {/* Animated Mesh Background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs */}
          <motion.div
            animate={{
              x: [0, 100, 0],
              y: [0, -50, 0],
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-20 left-20 w-96 h-96 bg-orange-500/30 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              x: [0, -100, 0],
              y: [0, 100, 0],
            }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-20 right-20 w-96 h-96 bg-orange-500/30 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              x: [0, 50, 0],
              y: [0, -100, 0],
            }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            className="absolute top-1/2 left-1/2 w-96 h-96 bg-white/30 rounded-full blur-3xl"
          />

          {/* Animated grid overlay */}
          <motion.div
            animate={{
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>

        <div className="max-w-[1800px] mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Left - Title & Info */}
            <div>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-orange-500 text-sm tracking-widest mb-6 block font-silkscreen"
              >
                {CONTACT_DATA.hero.label}
              </motion.span>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.85] tracking-tight mb-6 sm:mb-8 font-silkscreen"
              >
                {CONTACT_DATA.hero.title}<br />
                <span className="text-white/30">{CONTACT_DATA.hero.titleLine2}</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-white/60 text-sm sm:text-base lg:text-lg leading-relaxed max-w-lg mb-8 sm:mb-12"
              >
                {CONTACT_DATA.hero.description}
              </motion.p>

              {/* Contact Info Cards */}
              <div className="space-y-4 mb-10">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  whileHover={{ x: 10 }}
                  className="bg-[#151515] hover:bg-[#1a1a1a] rounded-[24px] p-6 border border-white/5 hover:border-orange-500/30 transition-all cursor-pointer flex items-center gap-5"
                >
                  <div className="w-14 h-14 bg-orange-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <span className="text-white/40 text-xs tracking-widest block mb-1 font-silkscreen">EMAIL</span>
                    <span className="text-white font-bold">{CONTACT_DATA.info.email}</span>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  whileHover={{ x: 10 }}
                  className="bg-[#151515] hover:bg-[#1a1a1a] rounded-[24px] p-6 border border-white/5 hover:border-orange-500/30 transition-all cursor-pointer flex items-center gap-5"
                >
                  <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <span className="text-white/40 text-xs tracking-widest block mb-1 font-silkscreen">LOCATION</span>
                    <span className="text-white font-bold">{CONTACT_DATA.info.location}</span>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  whileHover={{ x: 10 }}
                  className="bg-[#151515] hover:bg-[#1a1a1a] rounded-[24px] p-6 border border-white/5 hover:border-orange-500/30 transition-all cursor-pointer flex items-center gap-5"
                >
                  <div className="w-14 h-14 bg-green-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <span className="text-white/40 text-xs tracking-widest block mb-1 font-silkscreen">PHONE</span>
                    <span className="text-white font-bold">{CONTACT_DATA.info.phone}</span>
                  </div>
                </motion.div>
              </div>

              {/* Social Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex gap-4"
              >
                {CONTACT_DATA.socials.map((social, idx) => {
                  const Icon = social.icon;
                  return (
                    <motion.a
                      key={idx}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      whileTap={{ scale: 0.9 }}
                      className="w-14 h-14 bg-[#151515] hover:bg-orange-500 rounded-2xl flex items-center justify-center border border-white/5 hover:border-orange-500 transition-all group"
                    >
                      <Icon className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
                    </motion.a>
                  );
                })}
              </motion.div>

              {/* Download CV Button */}
              <motion.a
                href="/images/Bekirhan_Simsek_Resume.pdf"
                download="Bekirhan_Simsek_Resume.pdf"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-8 bg-white/10 hover:bg-white text-white hover:text-black px-6 py-4 rounded-full transition-all flex items-center gap-3 border border-white/20 hover:border-white font-silkscreen inline-flex"
              >
                <Download className="w-5 h-5" />
                DOWNLOAD CV
              </motion.a>
            </div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="bg-[#151515] rounded-[40px] p-8 lg:p-[54px] border border-white/5 ">
                <span className="text-orange-500 text-sm tracking-widest mb-8 block font-silkscreen">
                  SEND A MESSAGE
                </span>

                {submitted ? (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-center py-16"
                  >
                    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-500" />
                    </div>
                    <h3 className="text-3xl font-bold mb-4 font-silkscreen">
                      SENT!
                    </h3>
                    <p className="text-white/60">
                      Thank you for your message. I'll get back to you soon.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                          FIRST NAME *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          className="w-full bg-[#0a0a0a] border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                          LAST NAME *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          className="w-full bg-[#0a0a0a] border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors"
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                        EMAIL *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-[#0a0a0a] border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                        PHONE
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-[#0a0a0a] border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors"
                        placeholder="+32 123 456 789"
                      />
                    </div>

                    <div>
                      <label className="text-white/40 text-xs tracking-widest mb-2 block font-silkscreen">
                        MESSAGE *
                      </label>
                      <textarea
                        required
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full bg-[#0a0a0a] border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-orange-500 transition-colors resize-none"
                        placeholder="Tell me about your project..."
                      />
                    </div>

                    <motion.button
                      type="submit"
                      disabled={loading}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white rounded-2xl px-8 py-4 font-bold transition-all flex items-center justify-center gap-3 font-silkscreen"
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          SEND MESSAGE
                          <Send className="w-5 h-5" />
                        </>
                      )}
                    </motion.button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    

      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-12 overflow-hidden relative">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.15, 0.1],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 -left-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-1/4 -right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-[1800px] mx-auto rounded-[48px] bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-[#0a0a0a] p-8 sm:p-12 lg:p-16 border border-white/10 relative overflow-hidden">
          {/* Decorative grid pattern */}
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 relative z-10">
            {/* Left - Title with enhanced styling */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="h-1 bg-gradient-to-r from-orange-500 to-transparent mb-8"
              />

              <span className="text-orange-500 text-sm tracking-widest mb-6 block font-silkscreen">
                FREQUENTLY ASKED
              </span>

              <h2 className="text-5xl lg:text-7xl font-bold font-silkscreen mb-6">
                <span className="bg-gradient-to-r from-white via-orange-500/50 to-white bg-clip-text text-transparent">
                  FAQ
                </span>
              </h2>

              <p className="text-white/60 text-base lg:text-lg leading-relaxed max-w-md">
                Find answers to common questions about working with me, project timelines, and collaboration.
              </p>

              {/* Decorative element */}
              <motion.div
                animate={{
                  rotate: [0, 360],
                }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-10 -left-10 w-32 h-32 border border-orange-500/20 rounded-full"
              />
              <motion.div
                animate={{
                  rotate: [360, 0],
                }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-5 -left-5 w-20 h-20 border border-orange-500/10 rounded-full"
              />
            </motion.div>

            {/* Right - FAQ Accordion with enhanced cards */}
            <div className="space-y-4 font-not">
              {CONTACT_DATA.faq.map((faq, idx) => (
                <FAQItem key={idx} faq={faq} index={idx} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {/* Stat 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-[24px] p-6 lg:p-8 border border-white/10 hover:border-orange-500/50 transition-all cursor-default group relative overflow-hidden"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
              />
              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="text-3xl lg:text-5xl font-silkscreen bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent mb-2"
                >
                  &lt;24h
                </motion.div>
                <p className="text-white/60 text-xs lg:text-sm">Average Response Time</p>
              </div>
            </motion.div>

            {/* Stat 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-[24px] p-6 lg:p-8 border border-white/10 hover:border-blue-500/50 transition-all cursor-default group relative overflow-hidden"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
              />
              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, type: "spring" }}
                  className="text-3xl lg:text-5xl font-silkscreen bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent mb-2"
                >
                  100%
                </motion.div>
                <p className="text-white/60 text-xs lg:text-sm">Project Commitment</p>
              </div>
            </motion.div>

            {/* Stat 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-[24px] p-6 lg:p-8 border border-white/10 hover:border-green-500/50 transition-all cursor-default group relative overflow-hidden"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
              />
              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, type: "spring" }}
                  className="text-3xl lg:text-5xl font-silkscreen bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent mb-2"
                >
                  8+
                </motion.div>
                <p className="text-white/60 text-xs lg:text-sm">Succesful projects</p>
              </div>
            </motion.div>

            {/* Stat 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-[24px] p-6 lg:p-8 border border-white/10 hover:border-purple-500/50 transition-all cursor-default group relative overflow-hidden"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"
              />
              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5, type: "spring" }}
                  className="text-3xl lg:text-5xl font-silkscreen bg-gradient-to-r from-purple-500 to-purple-600 bg-clip-text text-transparent mb-2"
                >
                  4/5★
                </motion.div>
                <p className="text-white/60 text-xs lg:text-sm">Client Satisfaction</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16 px-6 lg:px-12 relative overflow-hidden">
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 0.5, 0],
              }}
              transition={{
                duration: 3 + i,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeInOut"
              }}
              className="absolute w-2 h-2 bg-orange-400/60 rounded-full blur-sm"
              style={{
                left: `${20 + i * 20}%`,
                bottom: 0,
              }}
            />
          ))}
        </div>

        <div className="max-w-[1800px] mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-gradient-to-br from-orange-600/80 via-orange-700/70 to-orange-800/80 rounded-[40px] overflow-hidden"
          >
            {/* Animated gradient overlay */}
            <motion.div
              animate={{
                backgroundPosition: ['0% 0%', '100% 100%'],
              }}
              transition={{ duration: 10, repeat: Infinity, repeatType: 'reverse' }}
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: 'linear-gradient(45deg, transparent 25%, rgba(255,255,255,0.1) 50%, transparent 75%)',
                backgroundSize: '200% 200%',
              }}
            />

            {/* Decorative geometric shapes */}
            <div className="absolute inset-0 overflow-hidden opacity-10">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute -top-20 -right-20 w-64 h-64 border-4 border-white rounded-full"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-20 -left-20 w-64 h-64 border-4 border-white"
              />
            </div>

            {/* Content */}
            <div className="relative z-10 p-8 lg:p-12 text-center">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-2 h-2 bg-white rounded-full"
                />
                <span className="text-white text-xs tracking-widest font-silkscreen">
                  LET'S WORK TOGETHER
                </span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-4xl lg:text-5xl text-white mb-4 font-silkscreen leading-tight"
              >
                Ready to Start<br />
                <span className="inline-block">
                  <motion.span
                    animate={{ opacity: [1, 0.8, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-white drop-shadow-2xl"
                  >
                    Your Project?
                  </motion.span>
                </span>
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-white/90 text-sm lg:text-base max-w-xl mx-auto mb-8 leading-relaxed"
              >
                I'm available for freelance projects and collaborations. Let's discuss how we can work together to achieve your goals.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row gap-3 justify-center items-center"
              >
                <a href={`mailto:${CONTACT_DATA.info.email}`}>
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="group relative flex items-center justify-center gap-2 bg-white text-orange-700 px-8 py-3.5 rounded-full font-bold transition-all font-silkscreen shadow-2xl overflow-hidden text-sm"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white to-orange-50"
                      whileHover={{ scale: 1.1 }}
                    />
                    <span className="relative z-10">SEND EMAIL</span>
                    <ArrowUpRight className="relative z-10 w-4 h-4 group-hover:rotate-45 transition-transform" />
                  </motion.button>
                </a>
                <Link to="/projects">
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="group flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white px-8 py-3.5 rounded-full font-bold transition-all border-2 border-white/30 hover:border-white font-silkscreen text-sm"
                  >
                    VIEW PROJECTS
                    <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                  </motion.button>
                </Link>
              </motion.div>

              {/* Bottom decorative line */}
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="mt-8 h-1 bg-white/30 rounded-full max-w-md mx-auto"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative py-8 sm:py-12 overflow-hidden bg-gradient-to-b from-[#0a0a0a] to-black">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.03, 0.06, 0.03],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-20 left-1/4 w-96 h-96 bg-orange-500 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.02, 0.05, 0.02],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute -bottom-20 right-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl"
          />
        </div>

        <div className="relative">
          {/* Top line decoration */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent mb-6 sm:mb-8"
          />

          {/* Main marquee */}
          <div className="overflow-hidden">
            <motion.div
              animate={{ x: ["-50%", "0%"] }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              className="flex items-center gap-12 sm:gap-16 whitespace-nowrap"
            >
              {[...Array(10)].map((_, i) => (
                <React.Fragment key={i}>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <motion.span
                      className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-silkscreen bg-gradient-to-r from-white via-orange-500 to-white bg-clip-text text-transparent font-normal"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: "spring", stiffness: 400 }}
                      style={{
                        backgroundSize: '200% auto',
                      }}
                    >
                      BEKIRHAN SIMSEK
                    </motion.span>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                      className="w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full shadow-lg shadow-orange-500/50"
                    />
                  </div>
                </React.Fragment>
              ))}
            </motion.div>
          </div>

          {/* Bottom line decoration */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
            className="h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent mt-6 sm:mt-8"
          />
        </div>
      </section>

      {/* Back to Top Button with Sticky Label */}
      <BackToTopButton />
    </motion.div>
  );
}

export default ContactPage;